import React, { useEffect, useState } from "react";
import { toast } from "react-toastify";
import { getAdCtas, uploadAdMedia, getMySellerId, getMyProducts } from "../../hook/useAdsApi";
import {
  AD_DESTINATION_TYPES,
  OBJECTIVE_DESTINATION_MAP,
  CTA_ICONS,
  getCurrentUserId,
} from "./adsConstants";
import documentUploadIcon from "../../assets/images/icons/ads-document-upload.svg";
import closeCircleIcon from "../../assets/images/icons/ads-close-circle.svg";

export default function CreateAd({ formData, updateFormData, onNext, continueLabel = "Continue", submitting = false, adAccount }) {
  const [ctas, setCtas] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [mediaUrlInput, setMediaUrlInput] = useState("");
  const [myProducts, setMyProducts] = useState([]);
  const [productsLoading, setProductsLoading] = useState(false);
  const [productsError, setProductsError] = useState(null);

  // Product/Marketplace Listing destination needs the account's own products to pick from -
  // Creator/Brand and External Business accounts have no Seller profile and so no products at
  // all, so both are removed from the choices entirely for them.
  const hasSellerAccount = !!adAccount?.seller_id;
  const allowedDestinations = OBJECTIVE_DESTINATION_MAP[formData.objective] || [];
  const destinationOptions = AD_DESTINATION_TYPES.filter(
    (dest) =>
      allowedDestinations.includes(dest.value) &&
      (hasSellerAccount || (dest.value !== "product" && dest.value !== "marketplace_listing"))
  );
  const isProductDestination = formData.destinationType === "product" || formData.destinationType === "marketplace_listing";

  useEffect(() => {
    if (!formData.objective) return;
    getAdCtas(formData.objective)
      .then((result) => setCtas(result?.ctas || []))
      .catch(() => setCtas([]));
  }, [formData.objective]);

  useEffect(() => {
    if (formData.destinationType === "profile" && !formData.destinationId) {
      updateFormData({ destinationId: getCurrentUserId() });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Hiding Product/Marketplace Listing from the pills only prevents a *new* selection - if
  // destinationType was already one of them (e.g. the account changed after this was set), it
  // has to be force-cleared too so an invalid destination_type/destination_id pair can't still
  // be submitted.
  useEffect(() => {
    if (!hasSellerAccount && (formData.destinationType === "product" || formData.destinationType === "marketplace_listing")) {
      updateFormData({ destinationType: "", destinationId: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [hasSellerAccount]);

  // Product/Marketplace Listing destination both resolve to the same Product table on the
  // backend - /buyer/get-products has no auto-scoping, so this resolves the caller's own
  // seller_id first, then filters by it - fetched once, on first switching to either.
  useEffect(() => {
    if (!isProductDestination || myProducts.length > 0 || productsLoading) return;
    setProductsLoading(true);
    getMySellerId()
      .then((sellerId) => (sellerId ? getMyProducts(sellerId) : []))
      .then(setMyProducts)
      .catch(() => setProductsError("Couldn't load your products. Try again later."))
      .finally(() => setProductsLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isProductDestination]);

  const selectDestinationType = (value) => {
    // "profile" targets the logged-in user's own id automatically; product/live/marketplace
    // listing need a real destination_id typed in - client-api only checks that the id exists,
    // it doesn't require a picker UI.
    updateFormData({
      destinationType: value,
      destinationId: value === "profile" ? getCurrentUserId() : value === "track" ? formData.soundId : "",
    });
  };

  // client-api's creative_media schema is min(1).max(1) - exactly one item, on both
  // create and update - so a new pick always replaces whatever was there, not appends.
  const handleFileChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 1);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = await uploadAdMedia(files);
      updateFormData({ mediaFiles: uploaded });
    } catch {
      toast.error("Failed to upload media");
    } finally {
      setUploading(false);
      e.target.value = "";
    }
  };

  const IMAGE_EXTENSIONS = ["jpg", "jpeg", "png", "gif", "webp"];
  const VIDEO_EXTENSIONS = ["mp4", "mov", "webm", "ogg"];

  // Testing-only fallback: the /aws/generate-presigned-url upload currently fails
  // with an AWS InvalidAccessKeyId error (backend credentials issue, not fixable here),
  // so this lets a URL be added directly without going through S3 upload. Only rejects a
  // clear mismatch (e.g. pasting a .mp4 while Image is selected) - many real image URLs
  // (Picsum, Unsplash-style, dynamic thumbnail services) have no file extension at all, so
  // an absent extension isn't itself treated as suspicious.
  const addMediaByUrl = () => {
    const url = mediaUrlInput.trim();
    if (!url) return;

    const extensionMatch = url.split(/[?#]/)[0].match(/\.([a-zA-Z0-9]+)$/);
    const extension = extensionMatch?.[1]?.toLowerCase();
    const wrongTypeExtensions = formData.creativeType === "video" ? IMAGE_EXTENSIONS : VIDEO_EXTENSIONS;

    if (extension && wrongTypeExtensions.includes(extension)) {
      toast.error(
        formData.creativeType === "video"
          ? "That looks like an image URL, not a video - it'll show broken if added."
          : "That looks like a video URL, not an image - it'll show broken if added."
      );
      return;
    }

    updateFormData({ mediaFiles: [{ url, media_type: formData.creativeType }] });
    setMediaUrlInput("");
  };

  const removeMedia = () => {
    updateFormData({ mediaFiles: [] });
  };

  // client-api rejects a video ad with no thumbnail (a separate poster-image, distinct from
  // the video file itself - thumbnailSchema requires media_type:'image' strictly, and the
  // service layer requires it outright whenever creative_type is 'video').
  const [thumbnailUploading, setThumbnailUploading] = useState(false);
  const [thumbnailUrlInput, setThumbnailUrlInput] = useState("");

  const handleThumbnailFileChange = async (e) => {
    const files = Array.from(e.target.files || []).slice(0, 1);
    if (files.length === 0) return;
    setThumbnailUploading(true);
    try {
      const uploaded = await uploadAdMedia(files);
      updateFormData({ thumbnail: uploaded[0] });
    } catch {
      toast.error("Failed to upload thumbnail");
    } finally {
      setThumbnailUploading(false);
      e.target.value = "";
    }
  };

  const addThumbnailByUrl = () => {
    const url = thumbnailUrlInput.trim();
    if (!url) return;

    const extensionMatch = url.split(/[?#]/)[0].match(/\.([a-zA-Z0-9]+)$/);
    const extension = extensionMatch?.[1]?.toLowerCase();
    if (extension && VIDEO_EXTENSIONS.includes(extension)) {
      toast.error("That looks like a video URL, not an image - it'll show broken if added.");
      return;
    }

    updateFormData({ thumbnail: { url, media_type: "image" } });
    setThumbnailUrlInput("");
  };

  const removeThumbnail = () => updateFormData({ thumbnail: null });

  const canContinue =
    formData.adName.trim() &&
    formData.mediaFiles.length > 0 &&
    (formData.creativeType !== "video" || formData.thumbnail) &&
    formData.headline.trim() &&
    formData.description.trim() &&
    formData.cta &&
    formData.destinationType &&
    formData.destinationId;

  return (
    <div>
      {/* Colors/typography carried over from Figma's Step 1 & account screens (read-only
          access) - Create Ad itself isn't in the Figma file, so this matches the established
          design language rather than an exact per-field spec. */}
      <label className="block text-sm font-semibold text-black mb-1">Ad Name</label>
      <input
        type="text"
        value={formData.adName}
        onChange={(e) => updateFormData({ adName: e.target.value })}
        placeholder="Enter Ad Name"
        className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-6"
      />

      <label className="block text-sm font-semibold text-black mb-2">Images/videos.</label>
      <div className="flex gap-3 mb-4">
        {["image", "video"].map((t) => (
          <button
            key={t}
            type="button"
            onClick={() => updateFormData({ creativeType: t })}
            className={`px-5 py-2 rounded-full text-sm font-semibold capitalize ${
              formData.creativeType === t
                ? "bg-[#095228] text-white"
                : "border border-[#c3c5d9] text-[#434656]"
            }`}
          >
            {t}
          </button>
        ))}
      </div>

      <label className="block text-sm font-semibold text-black mb-2">
        {formData.creativeType === "video" ? "Upload video" : "Upload images"}
      </label>
      <label className="flex items-center justify-between border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2 cursor-pointer text-[#747474]">
        <span>
          {uploading
            ? "Uploading..."
            : formData.creativeType === "video"
            ? "Upload video."
            : "Upload images."}
        </span>
        <input
          type="file"
          accept={formData.creativeType === "image" ? "image/*" : "video/*"}
          onChange={handleFileChange}
          disabled={uploading}
          className="hidden"
        />
        <img src={documentUploadIcon} alt="" className="w-6 h-6" />
      </label>

      <div className="flex gap-2 mb-2">
        <input
          type="text"
          value={mediaUrlInput}
          onChange={(e) => setMediaUrlInput(e.target.value)}
          placeholder={
            formData.creativeType === "video"
              ? "Or paste a video URL (testing only - upload is currently broken)"
              : "Or paste an image URL (testing only - upload is currently broken)"
          }
          className="flex-1 min-w-0 border border-[#c3c5d9] rounded-xl px-3 py-2 text-sm"
        />
        <button
          type="button"
          onClick={addMediaByUrl}
          className="px-4 py-2 rounded-xl border border-[#095228] text-[#095228] text-sm font-semibold"
        >
          Add
        </button>
      </div>

      {formData.mediaFiles.length > 0 && (
        <div className="flex gap-3 mb-2">
          {formData.mediaFiles.map((media) => (
            <div key={media.url} className="relative">
              {media.media_type === "video" ? (
                <video src={media.url} controls className="w-20 h-20 object-cover rounded-xl" />
              ) : (
                <img src={media.url} alt="" className="w-20 h-20 object-cover rounded-xl" />
              )}
              <button type="button" onClick={removeMedia} className="absolute -top-2 -right-2">
                <img src={closeCircleIcon} alt="Remove" className="w-5 h-5" />
              </button>
            </div>
          ))}
        </div>
      )}
      <p className="text-xs text-[#747474] mb-6">
        {formData.creativeType === "video" ? "MP4 or MOV - one video per ad" : "JPG or PNG - one image per ad"}
      </p>

      {formData.creativeType === "video" && (
        <>
          {/* client-api requires a separate poster-image thumbnail for every video ad - the
              video file itself can't be used as its own thumbnail (thumbnailSchema only
              accepts media_type:'image'). */}
          <label className="block text-sm font-semibold text-black mb-2">Thumbnail Image</label>
          <label className="flex items-center justify-between border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2 cursor-pointer text-[#747474]">
            <span>{thumbnailUploading ? "Uploading..." : "Upload a thumbnail image."}</span>
            <input
              type="file"
              accept="image/*"
              onChange={handleThumbnailFileChange}
              disabled={thumbnailUploading}
              className="hidden"
            />
            <img src={documentUploadIcon} alt="" className="w-6 h-6" />
          </label>
          <div className="flex gap-2 mb-2">
            <input
              type="text"
              value={thumbnailUrlInput}
              onChange={(e) => setThumbnailUrlInput(e.target.value)}
              placeholder="Or paste a thumbnail image URL (testing only - upload is currently broken)"
              className="flex-1 border border-[#c3c5d9] rounded-xl px-3 py-2 text-sm"
            />
            <button
              type="button"
              onClick={addThumbnailByUrl}
              className="px-4 py-2 rounded-xl border border-[#095228] text-[#095228] text-sm font-semibold"
            >
              Add
            </button>
          </div>
          {formData.thumbnail && (
            <div className="flex gap-3 mb-2">
              <div className="relative">
                <img src={formData.thumbnail.url} alt="" className="w-20 h-20 object-cover rounded-xl" />
                <button type="button" onClick={removeThumbnail} className="absolute -top-2 -right-2">
                  <img src={closeCircleIcon} alt="Remove" className="w-5 h-5" />
                </button>
              </div>
            </div>
          )}
          <p className="text-xs text-[#747474] mb-6">Required for video ads - JPG or PNG poster image.</p>
        </>
      )}

      <label className="block text-sm font-semibold text-black mb-1">Ad Headline</label>
      <input
        type="text"
        value={formData.headline}
        onChange={(e) => updateFormData({ headline: e.target.value })}
        placeholder="Enter Ad Headline"
        maxLength={150}
        className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3"
      />
      <p className="text-xs text-[#9a9a9a] text-right mb-4">{formData.headline.length}/150</p>

      <label className="block text-sm font-semibold text-black mb-1">Ad Description</label>
      <textarea
        value={formData.description}
        onChange={(e) => updateFormData({ description: e.target.value })}
        placeholder="Enter Description"
        rows={5}
        maxLength={2000}
        className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3"
      />
      <p className="text-xs text-[#9a9a9a] text-right mb-4">{formData.description.length}/2000</p>

      <label className="block text-sm font-semibold text-black mb-2">Call to Action</label>
      <div className="flex flex-wrap gap-2 mb-6">
        {ctas.map((cta) => {
          const selected = formData.cta === cta.code;
          const icon = CTA_ICONS[cta.code];
          return (
            <button
              key={cta.code}
              type="button"
              onClick={() => updateFormData({ cta: cta.code })}
              className={`flex items-center gap-2 px-4 py-2 rounded-full text-sm font-semibold ${
                selected ? "bg-[#095228] text-white" : "border border-[#c3c5d9] text-[#434656]"
              }`}
            >
              {selected && icon && <img src={icon} alt="" className="w-4 h-4" />}
              {cta.label}
            </button>
          );
        })}
      </div>

      <label className="block text-sm font-semibold text-black mb-2">Destination Type</label>
      <div className="flex flex-wrap gap-2 mb-2">
        {destinationOptions.map((dest) => (
          <button
            key={dest.value}
            type="button"
            onClick={() => selectDestinationType(dest.value)}
            className={`px-4 py-2 rounded-full text-sm font-semibold ${
              formData.destinationType === dest.value
                ? "bg-[#095228] text-white"
                : "border border-[#c3c5d9] text-[#434656]"
            }`}
          >
            {dest.label}
          </button>
        ))}
      </div>
      {isProductDestination && (
        <>
          {productsError && <p className="text-red-500 text-xs mb-2">{productsError}</p>}
          <select
            value={formData.destinationId}
            onChange={(e) => updateFormData({ destinationId: e.target.value })}
            className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2 text-sm"
          >
            <option value="">{productsLoading ? "Loading your products..." : "Select a product"}</option>
            {myProducts.map((p) => (
              <option key={p.id} value={p.id}>{p.product_name}</option>
            ))}
          </select>
          <p className="text-xs text-[#747474] mb-10">
            {!productsLoading && !productsError && myProducts.length === 0
              ? "You don't have any product listings yet."
              : "Choose one of your own product listings."}
          </p>
        </>
      )}
      {formData.destinationType === "live" && (
        <>
          {/* No listing endpoint exposes the plain numeric LiveStream id needed here (only a
              separate stream_id string used elsewhere in the live module) - entered directly
              until that's exposed. */}
          <input
            type="number"
            value={formData.destinationId}
            onChange={(e) => updateFormData({ destinationId: e.target.value })}
            placeholder="Enter Live Stream ID"
            className="w-full border border-[#c3c5d9] rounded-xl px-4 py-3 mb-2"
          />
          <p className="text-xs text-[#747474] mb-10">The numeric ID of one of your own live streams.</p>
        </>
      )}
      {formData.destinationType === "track" && (
        <p className="text-xs text-[#747474] mb-10">
          Your sound (ID {formData.destinationId}). Only your own published, public sound that is open for licensing can be promoted.
        </p>
      )}
      {(!formData.destinationType || formData.destinationType === "profile") && <div className="mb-8" />}

      <button
        type="button"
        disabled={!canContinue || submitting}
        onClick={onNext}
        className="bg-[#095228] border border-[#39b54a] text-[#f9e9ea] font-medium text-lg px-10 py-3 rounded-full shadow-[0_0_6px_rgba(59,182,76,0.53)] disabled:opacity-50"
      >
        {submitting ? "Saving..." : continueLabel}
      </button>
    </div>
  );
}
